import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n/useT';
import { useCurrency } from '../../context/CurrencyContext';
import { fmt } from '../../utils/format';
import { F } from '../../styles/theme';
import { Segmented } from './Editorial';
import { BigAreaChart } from './Charts';

const TNUM = { fontFeatureSettings: '"tnum" 1, "lnum" 1' };

// Native iOS: relative /api/... würde auf capacitor://localhost/api/... gehen (kein Server).
const _apiBase = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
  ? 'https://app.redcrypto.de' : '';

// ── Cache helper (1h TTL) ───────────────────────────────────────────────────
const CACHE_TTL_MS = 60 * 60 * 1000;
function cacheGet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || Date.now() - (obj.t || 0) > CACHE_TTL_MS) return null;
    return obj.v;
  } catch { return null; }
}
function cacheSet(key, v) {
  try { localStorage.setItem(key, JSON.stringify({ t: Date.now(), v })); } catch {}
}

// ── Animated count-up (cubic ease-out) ──────────────────────────────────────
function useAnimatedValue(target, duration = 1100) {
  const [v, setV] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (target == null) return;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(target * eased);
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return v;
}

// ── Unified signature: the calibrated Reading Bar ────────────────────────────
// Calm-Redesign 2026-06-10: statt vier fremder Hero-Bilder (Bogen/Donut/Matrix/
// Treemap) liest sich jeder Index gleich — ein großer Wert, daneben die Deutung
// in Klartext, darunter EINE kalibrierte Lese-Skala mit präzisem Marker. Die
// farbige Zone, in der der Marker steht, IST die Aussage. Akzent nur via Fill,
// keine rot-getönten Flächen; Zonen-Farben ziehen aus dem Theme (calm).

// Skalen-Hex → Theme-Token (warm/calm). Marken-Rot bleibt der Marke: negative
// Zonen nutzen T.loss, nicht das harte #E4544A.
const ZONE_TOKEN = { '#E4544A': 'loss', '#E2814B': 'gold', '#9090A0': 'sub', '#82C46C': 'gain', '#2ECC71': 'gain' };
function zoneColor(hex, T) { const k = ZONE_TOKEN[hex]; return (k && T[k]) || hex; }

// DIE Signatur — horizontale, kalibrierte Skala 0–100 mit Zonen-Segmenten,
// Tick-Marken an den Grenzen, präzisem Marker (Knopf + Stiel) und Wert-Fähnchen.
function ReadingBar({ scale, value, T, loLabel, hiLabel }) {
  const v = Math.max(0, Math.min(100, value));
  const cls = classify(scale, value);
  const mc = zoneColor(cls.color, T);
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ position: 'relative', height: 30 }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: 13, height: 6, borderRadius: 999, background: T.line }} />
        {scale.map((z, i) => {
          const lo = i === 0 ? 0 : scale[i - 1].max;
          const hi = z.max >= 100 ? 100 : z.max;
          const active = v > lo && v <= hi;
          return (
            <div key={i} style={{
              position: 'absolute', top: 13, height: 6, left: `${lo}%`,
              width: `${Math.max(0, hi - lo - 1)}%`, borderRadius: 999,
              background: zoneColor(z.color, T), opacity: active ? 0.78 : 0.34,
              transition: 'opacity 400ms ease',
            }} />
          );
        })}
        {scale.slice(0, -1).map((z, i) => (
          <div key={'t' + i} style={{ position: 'absolute', top: 23, left: `${z.max}%`, width: 1, height: 5, background: T.lineSoft || T.line }} />
        ))}
        <div style={{ position: 'absolute', top: 6, left: `${v}%`, width: 2, height: 10, transform: 'translateX(-1px)', background: mc, opacity: 0.45, transition: 'left 700ms cubic-bezier(.16,1,.3,1)' }} />
        <div style={{
          position: 'absolute', top: -4, left: `${v}%`, transform: 'translateX(-50%)',
          fontFamily: F.display, fontSize: 12.5, fontWeight: 800, color: T.text,
          background: T.bg, padding: '0 5px', whiteSpace: 'nowrap', ...TNUM,
        }}>{Math.round(value)}</div>
        <div style={{
          position: 'absolute', top: 16, left: `${v}%`, width: 15, height: 15, borderRadius: '50%',
          transform: 'translate(-50%,-50%)', background: T.bg, border: `3px solid ${mc}`, zIndex: 2,
          transition: 'left 700ms cubic-bezier(.16,1,.3,1)',
        }} />
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', fontFamily: F.family,
        fontSize: 12, fontWeight: 600, color: T.sub, marginTop: 11,
      }}>
        <span>{loLabel}</span><span>{hiLabel}</span>
      </div>
    </div>
  );
}

// Einheitlicher Hero: großer Wert + Einheit links, Klartext-Deutung rechts.
// `word` = farbiges Deutungswort (oder Node), `sub` = kleine Zeile darunter.
function SheetHero({ num, unit, word, wordColor, sub, T }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, marginTop: 20 }}>
      <div style={{
        fontFamily: F.display, fontSize: 60, fontWeight: 800, letterSpacing: '-0.055em',
        color: T.text, lineHeight: 0.82, animation: 'miNumIn 700ms ease-out', ...TNUM,
      }}>
        {num}{unit && <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: T.sub, marginLeft: 6 }}>{unit}</span>}
      </div>
      <div style={{ textAlign: 'right', paddingBottom: 6, minWidth: 0 }}>
        {word != null && (
          <div style={{ fontFamily: F.display, fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em', color: wordColor || T.text, lineHeight: 1 }}>
            {word}
          </div>
        )}
        {sub != null && (
          <div style={{ fontFamily: F.family, fontSize: 13, fontWeight: 500, color: T.sub, marginTop: 6, ...TNUM }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

// Klartext-Delta-Zeile für den Hero („▲ 2 ggü. gestern"). Gibt einen Node zurück.
function DeltaSub({ delta, T, t }) {
  if (delta == null || !isFinite(delta)) return null;
  const pos = delta >= 0;
  const c = pos ? (T.gain || T.green) : (T.loss || T.red);
  const val = Math.abs(delta);
  const shown = val >= 10 ? val.toFixed(0) : val.toFixed(1);
  return (
    <span>
      <span style={{ color: c, fontWeight: 700 }}>{pos ? '▲' : '▼'} {shown}</span> {t('wid_vs_yesterday')}
    </span>
  );
}

// Performance-Balken (Bar-Race) für Top-Outperformer.
function PerfBar({ rank, symbol, perf, max, T }) {
  const pct = max > 0 ? Math.max(5, Math.min(100, (Math.abs(perf) / max) * 100)) : 5;
  const pos = perf >= 0;
  const c = pos ? T.green : T.loss;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0' }}>
      <span style={{ fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: T.mute, minWidth: 16, ...TNUM }}>
        {String(rank).padStart(2, '0')}
      </span>
      <span style={{ fontFamily: F.display, fontSize: 14, fontWeight: 700, letterSpacing: '-0.02em', color: T.text, minWidth: 54 }}>
        {symbol}
      </span>
      <div style={{ flex: 1, height: 8, background: T.line, borderRadius: 999, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%', background: c, borderRadius: 999,
          transformOrigin: 'left', transform: 'scaleX(0)',
          animation: `miGrow 620ms ${120 + rank * 70}ms cubic-bezier(.16,1,.3,1) forwards`,
        }} />
      </div>
      <span style={{ fontFamily: F.display, fontSize: 14, fontWeight: 700, color: c, letterSpacing: '-0.02em', minWidth: 56, textAlign: 'right', ...TNUM }}>
        {pos ? '+' : '−'}{Math.abs(perf).toFixed(1)}%
      </span>
    </div>
  );
}

// ── Editorial line chart (single color, ringed extrema) ─────────────────────
function HistoryLine({ data, color, T, width = 320, height = 72, minY = 0, maxY = 100 }) {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => {
    const x = i * step;
    const y = height - ((v - minY) / (maxY - minY)) * (height - 14) - 8;
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const baselineY = height - ((50 - minY) / (maxY - minY)) * (height - 14) - 8;
  const dMin = Math.min(...data), dMax = Math.max(...data);
  const minIdx = data.indexOf(dMin), maxIdx = data.indexOf(dMax);
  const minPt = pts[minIdx], maxPt = pts[maxIdx], lastPt = pts[pts.length - 1];

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      {50 >= minY && 50 <= maxY && (
        <line x1="0" y1={baselineY} x2={width} y2={baselineY}
              stroke={T.line} strokeWidth="0.5" strokeDasharray="2 4" />
      )}
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={minPt[0]} cy={minPt[1]} r="2.5" fill="none" stroke={T.sub} strokeWidth="1" />
      <circle cx={maxPt[0]} cy={maxPt[1]} r="2.5" fill="none" stroke={T.sub} strokeWidth="1" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="3.2" fill={color}
              stroke={T.bg} strokeWidth="1.5" />
    </svg>
  );
}

// ── Classification scales ───────────────────────────────────────────────────
const FG_SCALE = [
  { max: 24,  labelKey: 'wid_fg_extreme_fear', shortKey: 'wid_fg_extreme_fear_s', color: '#E4544A' },
  { max: 44,  labelKey: 'wid_fg_fear',         shortKey: 'wid_fg_fear_s',         color: '#E2814B' },
  { max: 55,  labelKey: 'wid_neutral',         shortKey: 'wid_neutral_s',         color: '#9090A0' },
  { max: 74,  labelKey: 'wid_fg_greed',        shortKey: 'wid_fg_greed_s',        color: '#82C46C' },
  { max: 100, labelKey: 'wid_fg_extreme_greed', shortKey: 'wid_fg_extreme_greed_s', color: '#2ECC71' },
];
// Higher dominance = stronger BTC era
const BTCD_SCALE = [
  { max: 39.99, labelKey: 'wid_btcd_weak',     shortKey: 'wid_btcd_alt_phase_s', color: '#82C46C' },
  { max: 49.99, labelKey: 'wid_neutral',       shortKey: 'wid_neutral_s',        color: '#9090A0' },
  { max: 59.99, labelKey: 'wid_btcd_strong',   shortKey: 'wid_btcd_strong_s',    color: '#E2814B' },
  { max: 100,   labelKey: 'wid_btcd_dominant', shortKey: 'wid_btcd_dominant_s',  color: '#E4544A' },
];
// Higher altseason = more altcoins beating BTC
const ALT_SCALE = [
  { max: 24,  labelKey: 'wid_alt_btc_season',  shortKey: 'wid_alt_btc_season_s', color: '#E4544A' },
  { max: 49,  labelKey: 'wid_alt_btc_pref',    shortKey: 'wid_btcd_strong_s',    color: '#E2814B' },
  { max: 74,  labelKey: 'wid_alt_mixed',       shortKey: 'wid_alt_mixed_s',      color: '#9090A0' },
  { max: 100, labelKey: 'wid_alt_altseason',   shortKey: 'wid_alt_altseason_s',  color: '#2ECC71' },
];
function classify(scale, v) {
  return scale.find(c => v <= c.max) || scale[scale.length - 1];
}

// ── Shared header tile ──────────────────────────────────────────────────────
function IndexTile({ label, displayVal, cls, value, scale, T, t, onClick, disabled, withDivider = true, block = false, align }) {
  const [pressed, setPressed] = useState(false);
  const cc = cls ? zoneColor(cls.color, T) : T.mute;
  // Ausrichtung: explizit via align ('start'|'center'|'end'), sonst aus block.
  const al = align || (block ? 'start' : 'end');
  const flexAlign = al === 'center' ? 'center' : (al === 'start' ? 'flex-start' : 'flex-end');
  // Aktive Zone (für das Segment unter dem Punkt) aus der Skala bestimmen.
  let zlo = 0, zhi = 0;
  if (cls && scale) {
    const idx = scale.indexOf(cls);
    zlo = idx <= 0 ? 0 : scale[idx - 1].max;
    zhi = cls.max >= 100 ? 100 : cls.max;
  }
  // block = Mobile-Strip: gleichbreite Spalte, links ausgerichtet, mehr Luft.
  return (
    <button
      className="mo-row"
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      disabled={disabled}
      aria-label={`${label}: ${displayVal}${cls ? ', ' + t(cls.labelKey) : ''}`}
      style={{
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        flex: block ? 1 : 'none',
        width: al === 'center' ? '100%' : undefined,
        minWidth: 0,
        padding: al === 'center'
          ? '2px 18px'
          : block
            ? (withDivider ? '2px 0 2px 12px' : '2px 12px 2px 0')
            : (withDivider ? '4px 0 4px 10px' : '4px 0'),
        borderLeft: withDivider ? `1px solid ${T.line}` : 'none',
        cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: flexAlign,
        gap: block || al === 'center' ? 6 : 7,
        transition: 'transform 130ms ease-out, opacity 200ms ease',
        transform: pressed ? 'scale(0.97)' : 'scale(1)',
        opacity: disabled ? 0.45 : 1,
        textAlign: al === 'center' ? 'center' : (al === 'start' ? 'left' : 'right'),
      }}
    >
      <div style={{
        fontFamily: F.family, fontSize: 11, fontWeight: 500, lineHeight: 1,
        letterSpacing: '-0.005em', color: T.sub,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: F.display, fontSize: 26, fontWeight: 800,
        letterSpacing: '-0.045em', color: T.text, lineHeight: 1,
        ...TNUM,
      }}>
        {displayVal}
      </div>
      {/* Klartext-Status in der Klassifizierungs-Farbe (statt nur Bar-Farbe). */}
      {cls && (
        <div style={{
          fontFamily: F.family, fontSize: 11.5, fontWeight: 700,
          letterSpacing: '-0.01em', color: cc, lineHeight: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}>
          {t(cls.labelKey)}
        </div>
      )}
      {/* Mini-Lesebalken: dieselbe Signatur wie aufgeklappt — Skala mit aktiver
          Zone + präzisem Punkt. */}
      <div style={{
        position: 'relative', width: 64, height: 5, borderRadius: 999,
        background: T.line, marginTop: 2,
        alignSelf: flexAlign,
      }}>
        {scale && zhi > zlo && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0, left: `${zlo}%`,
            width: `${zhi - zlo}%`, borderRadius: 999, background: cc, opacity: 0.5,
          }} />
        )}
        <div style={{
          position: 'absolute', top: '50%',
          left: `${Math.max(0, Math.min(100, value))}%`,
          width: 11, height: 11, borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          background: T.bg, border: `2.5px solid ${cc}`,
          transition: 'left 240ms cubic-bezier(.16,1,.3,1)',
        }} />
      </div>
    </button>
  );
}

// ── Generic sheet skeleton ──────────────────────────────────────────────────
function SheetFrame({ title, subtitle, onClose, T, closeLabel = 'Close', children }) {
  // Hintergrund-Scroll sperren, solange das Sheet offen ist — sonst bewegt sich
  // der Markt-Screen hinter dem Sheet beim Hoch-/Runter-Scrollen. SheetFrame
  // mountet nur wenn open → Lock greift genau für die Offen-Dauer.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  // Portal nach document.body: das Sheet liegt sonst IM LateralPager-Track, der
  // transform/willChange:transform trägt → ein transform-Vorfahr wird zum
  // Containing-Block, position:fixed ankert am verschobenen Track statt am
  // Viewport (Sheet öffnet „zwischen" Dashboard & Markt). Portal löst es heraus.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 250,
        background: 'rgba(0,0,0,0.78)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'miFadeIn 220ms ease-out',
        touchAction: 'none',           // Touches auf dem Backdrop scrollen NICHT den Hintergrund (iOS)
      }}
    >
      <style>{`
        @keyframes miFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes miSlideUp { from { transform: translateY(28px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes miNumIn { from { letter-spacing: 0.04em; opacity: 0; } to { letter-spacing: -0.06em; opacity: 1; } }
        @keyframes miFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes miPop { 0% { opacity: 0; transform: scale(0.4); } 60% { opacity: 1; transform: scale(1.12); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes miGrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes miNeedle { from { transform: rotate(-14deg); opacity: 0; } to { transform: rotate(0deg); opacity: 0.85; } }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: T.bg,
          borderTopLeftRadius: 24, borderTopRightRadius: 24,
          borderTop: `1px solid ${T.line}`,
          padding: '14px 24px 30px',
          maxHeight: '92vh', overflowY: 'auto',
          touchAction: 'pan-y', overscrollBehavior: 'contain',   // Sheet scrollt intern, kein Scroll-Chaining auf den Hintergrund
          animation: 'miSlideUp 340ms cubic-bezier(0.22, 1, 0.36, 1)',
          boxShadow: T.isDark
            ? '0 -22px 60px rgba(0,0,0,0.6)'
            : '0 -22px 60px rgba(60,48,28,0.26)',
          position: 'relative',
        }}
      >
        <div style={{
          width: 40, height: 5, borderRadius: 999,
          background: T.line, margin: '0 auto 16px',
        }} />
        {/* Calm-Header: sentence-case Headline + neutraler runder Close. */}
        <div style={{ paddingRight: 44, marginBottom: 8 }}>
          <div style={{
            fontFamily: F.display, fontSize: 23, fontWeight: 800,
            letterSpacing: '-0.03em', color: T.text, lineHeight: 1.1,
          }}>{title}</div>
          {subtitle && (
            <div style={{ fontFamily: F.family, fontSize: 13, color: T.sub, marginTop: 4, ...TNUM }}>{subtitle}</div>
          )}
        </div>
        <button onClick={onClose} aria-label={closeLabel} className="mo-close" style={{
          position: 'absolute', top: 16, right: 18,
          width: 34, height: 34, borderRadius: '50%',
          background: T.surface, border: `1px solid ${T.line}`, color: T.sub,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', padding: 0, WebkitTapHighlightColor: 'transparent',
        }}>
          <svg className="mo-close-ico" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
}

// Klartext-Erklärung — flach, mit Haarlinie (calm: keine getönte Box).
function InfoCard({ T, title, children }) {
  return (
    <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${T.line}` }}>
      <div style={{
        fontFamily: F.family, fontSize: 13.5, fontWeight: 700,
        letterSpacing: '-0.01em', color: T.text, marginBottom: 7,
      }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: T.sub, lineHeight: 1.55, fontFamily: F.family }}>
        {children}
      </div>
    </div>
  );
}

function RefRow({ T, t, label, val, suffix = '', scale }) {
  const c = val != null && scale ? classify(scale, val) : null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '13px 0', borderBottom: `1px solid ${T.lineSoft || T.line}`,
    }}>
      <span style={{
        fontFamily: F.family, fontSize: 14, fontWeight: 500,
        letterSpacing: '-0.005em', color: T.sub,
      }}>
        {label}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 10 }}>
        {c && (
          <span style={{
            fontFamily: F.family, fontSize: 12.5, fontWeight: 600,
            color: zoneColor(c.color, T), letterSpacing: '-0.005em',
          }}>
            {t(c.shortKey)}
          </span>
        )}
        <span style={{
          fontFamily: F.display, fontSize: 17, fontWeight: 700,
          color: T.text, letterSpacing: '-0.03em', ...TNUM,
        }}>
          {val != null ? `${typeof val === 'number' ? val.toFixed(val % 1 ? 1 : 0) : val}${suffix}` : '–'}
        </span>
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEAR & GREED
// ─────────────────────────────────────────────────────────────────────────────
function FGSheet({ data, onClose, T, t }) {
  const cls = classify(FG_SCALE, data.value);
  const animVal = useAnimatedValue(data.value, 900);
  const hist = data.history;
  const yesterday = hist.length >= 2 ? hist[hist.length - 2] : null;
  const lastWeek  = hist.length >= 8 ? hist[hist.length - 8] : null;
  const lastMonth = hist.length >= 30 ? hist[0] : null;
  const dYesterday = yesterday != null ? data.value - yesterday : null;
  const updatedMin = data.timestamp
    ? Math.max(1, Math.round((Date.now() - data.timestamp * 1000) / 60000))
    : null;

  return (
    <SheetFrame T={T} onClose={onClose} closeLabel={t('wid_close')} title={t('wid_fg_title')}
                subtitle={updatedMin != null ? `${t('wid_fg_sub')} · ${t('wid_ago_min', { min: updatedMin })}` : t('wid_fg_sub')}>
      <SheetHero
        num={Math.round(animVal)}
        word={t(cls.labelKey)} wordColor={zoneColor(cls.color, T)}
        sub={dYesterday != null && isFinite(dYesterday) ? <DeltaSub delta={dYesterday} T={T} t={t} /> : null}
        T={T} />

      <ReadingBar scale={FG_SCALE} value={animVal} T={T}
                  loLabel={t('wid_scale_fear_lo')} hiLabel={t('wid_scale_greed_hi')} />

      <div style={{ marginTop: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <span style={{ fontFamily: F.display, fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em', color: T.text }}>
            {t('wid_history_30d')}
          </span>
          <span style={{ fontFamily: F.num || F.mono, fontSize: 12, color: T.sub, ...TNUM }}>
            {Math.min(...hist)} <span style={{ color: T.mute }}>–</span> {Math.max(...hist)}
          </span>
        </div>
        <HistoryLine data={hist} color={zoneColor(cls.color, T)} T={T} />
      </div>

      <div style={{ marginTop: 24 }}>
        <RefRow T={T} t={t} label={t('wid_yesterday')}   val={yesterday} scale={FG_SCALE} />
        <RefRow T={T} t={t} label={t('wid_last_week')}   val={lastWeek}  scale={FG_SCALE} />
        <RefRow T={T} t={t} label={t('wid_last_month')}  val={lastMonth} scale={FG_SCALE} />
      </div>

      <InfoCard T={T} title={t('wid_fg_info_title')}>
        {t('wid_fg_info_pre')}<strong style={{ color: T.text }}>0</strong>{t('wid_fg_info_mid')}
        <strong style={{ color: T.text }}>100</strong>{t('wid_fg_info_post')}
      </InfoCard>
      <div style={{
        marginTop: 16, fontFamily: F.family, fontSize: 11.5,
        color: T.faint || T.mute, textAlign: 'center', letterSpacing: '-0.005em',
      }}>{t('wid_source', { src: 'alternative.me' })}</div>
    </SheetFrame>
  );
}

function FearGreedTile({ T, t, withDivider, block, align }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const cached = cacheGet('mi_fg');
    if (cached) setData(cached);
    let alive = true;
    fetch('https://api.alternative.me/fng/?limit=30')
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        const list = Array.isArray(d?.data) ? d.data : [];
        if (!list.length) return;
        const latest = list[0];
        const history = list.map(x => parseInt(x.value, 10)).filter(n => isFinite(n)).reverse();
        const payload = {
          value: parseInt(latest.value, 10),
          classification: latest.value_classification,
          history,
          timestamp: parseInt(latest.timestamp, 10),
        };
        setData(payload);
        cacheSet('mi_fg', payload);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const target = data?.value ?? null;
  const animVal = useAnimatedValue(target, 1100);
  const value = target == null ? 0 : animVal;
  const cls = target == null ? null : classify(FG_SCALE, target);
  const displayVal = target == null ? '··' : Math.round(animVal);

  return (
    <>
      <IndexTile
        label="F&G"
        displayVal={displayVal}
        cls={cls}
        value={value}
        scale={FG_SCALE}
        T={T}
        t={t}
        onClick={() => data && setOpen(true)}
        disabled={!data}
        withDivider={withDivider}
        block={block}
        align={align}
      />
      {open && data && <FGSheet data={data} onClose={() => setOpen(false)} T={T} t={t} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BTC DOMINANCE
// ─────────────────────────────────────────────────────────────────────────────
function BtcDomSheet({ data, onClose, T, t }) {
  const { convert, symbol, locale, fxRate } = useCurrency();
  const cls = classify(BTCD_SCALE, data.btc);
  const animVal = useAnimatedValue(data.btc, 900);
  const dom24h = data.dom24h; // % change in dominance estimate (24h)
  // Global-Endpoint liefert USD → erst auf EUR-Basis bringen, dann convert()
  // (convert erwartet EUR-Input und gibt je nach Währung EUR oder USD zurück).
  const toEur = (usd) => (Number.isFinite(usd) && fxRate ? usd / fxRate : usd);
  const mcapStr = fmtBig(toEur(data.totalMcap), convert, symbol, locale, t);
  const volStr = fmtBig(toEur(data.totalVol), convert, symbol, locale, t);

  // Marktgewicht — Bitcoin-Gold, ETH-Silber, Stables-Grün, Rest neutral (calm).
  const segments = [
    { key: 'btc',     label: 'Bitcoin',            value: data.btc,     color: T.gold },
    { key: 'eth',     label: 'Ethereum',           value: data.eth,     color: T.silver },
    { key: 'stables', label: t('wid_stablecoins'), value: data.stables, color: T.gain || T.green },
    { key: 'rest',    label: t('wid_rest_market'), value: data.rest,    color: T.mute },
  ];

  return (
    <SheetFrame T={T} onClose={onClose} closeLabel={t('wid_close')} title={t('wid_btcd_title_calm')} subtitle={t('wid_btcd_sub')}>
      <SheetHero
        num={animVal.toFixed(1)} unit="%"
        word={t(cls.labelKey)} wordColor={zoneColor(cls.color, T)}
        sub={dom24h != null && isFinite(dom24h) ? <DeltaSub delta={dom24h} T={T} t={t} /> : null}
        T={T} />

      <ReadingBar scale={BTCD_SCALE} value={animVal} T={T}
                  loLabel={t('wid_scale_btcd_lo')} hiLabel={t('wid_scale_btcd_hi')} />

      <div style={{ marginTop: 26 }}>
        <div style={{ fontFamily: F.display, fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em', color: T.text, marginBottom: 12 }}>
          {t('wid_total_composition')}
        </div>
        <div style={{ display: 'flex', gap: 3, height: 14, borderRadius: 6, overflow: 'hidden', background: T.line }}>
          {segments.map((s) => (
            <div key={s.key} style={{ width: `${Math.max(0, s.value)}%`, height: '100%', background: s.color }} />
          ))}
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        {segments.map((s) => (
          <RefRow key={s.key} T={T} t={t}
            label={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <i style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />{s.label}
            </span>}
            val={`${s.value.toFixed(1)} %`} />
        ))}
        <RefRow T={T} t={t} label={t('wid_total_mcap')} val={mcapStr} />
        <RefRow T={T} t={t} label={t('wid_vol_24h')}    val={volStr} />
      </div>

      <InfoCard T={T} title={t('wid_btcd_info_title')}>
        {t('wid_btcd_info_body')}
      </InfoCard>
      <div style={{
        marginTop: 16, fontFamily: F.family, fontSize: 11.5,
        color: T.faint || T.mute, textAlign: 'center', letterSpacing: '-0.005em',
      }}>{t('wid_source', { src: 'CoinGecko' })}</div>
    </SheetFrame>
  );
}

// Großzahl-Formatter, währungsbewusst. n ist ein EUR-Betrag (CoinGecko-Proxy
// liefert per cgParam bereits die gewählte Währung über andere Pfade, hier aber
// kommt der Global-Endpoint in USD — siehe Hinweis im Aufrufer). convert/symbol/
// locale + t (für Mrd/Mio-Suffixe) werden injiziert, Layout bleibt identisch.
function fmtBig(n, convert, symbol, locale, t) {
  if (!isFinite(n)) return '–';
  const v = convert(n);
  if (v >= 1e12) return `${(v / 1e12).toFixed(2)} ${t('wid_unit_trillion')} ${symbol}`;
  if (v >= 1e9)  return `${(v / 1e9).toFixed(2)} ${t('wid_unit_billion')} ${symbol}`;
  if (v >= 1e6)  return `${(v / 1e6).toFixed(2)} ${t('wid_unit_million')} ${symbol}`;
  return `${fmt(v, locale)} ${symbol}`;
}

// Wie fmtBig, aber liefert Zahl und Einheit getrennt (für gestufte Typo) und
// formatiert die Zahl locale-bewusst (DE „3,42" statt „3.42").
function bigParts(n, convert, symbol, locale, t) {
  if (!isFinite(n)) return { num: '–', unit: '' };
  const v = convert(n);
  const f = (x, d) => x.toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d });
  if (v >= 1e12) return { num: f(v / 1e12, 2), unit: `${t('wid_unit_trillion')} ${symbol}` };
  if (v >= 1e9)  return { num: f(v / 1e9, 1),  unit: `${t('wid_unit_billion')} ${symbol}` };
  if (v >= 1e6)  return { num: f(v / 1e6, 0),  unit: `${t('wid_unit_million')} ${symbol}` };
  return { num: fmt(v, locale), unit: symbol };
}

// Proportionale „Marktgewicht"-Leiste: BTC-Gold, ETH-Silber, Stables-Grün, Rest neutral.
function MassBar({ pct, T, width = 168, height = 7 }) {
  const segs = [
    { v: pct.btc,     c: T.gold },
    { v: pct.eth,     c: T.silver },
    { v: pct.stables, c: T.green },
    { v: pct.rest,    c: T.mute },
  ];
  return (
    <div style={{ display: 'flex', width, height, gap: 2, borderRadius: 999, overflow: 'hidden', background: T.line }}>
      {segs.map((s, i) => (
        <div key={i} style={{
          width: `${Math.max(0, s.v)}%`, height: '100%', background: s.c, borderRadius: 2,
          transition: 'width 700ms cubic-bezier(.16,1,.3,1)',
        }} />
      ))}
    </div>
  );
}

// Richtungspfeil: ↗ (grün) oder ↙ (rot) + 24h-%-Zahl, ersetzt DeltaBlock rechts im Tile.
function TrendArrow({ delta, T }) {
  if (delta == null || !isFinite(delta)) return null;
  const pos = delta >= 0;
  const c = pos ? (T.gain || T.green) : (T.loss || T.red);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <svg viewBox="0 0 20 20" width={18} height={18} fill="none" aria-hidden="true"
        style={{ color: c, flexShrink: 0, transform: pos ? 'none' : 'rotate(90deg) scaleY(-1)' }}>
        <path d="M4 16L16 4M8 4h8v8" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span style={{ fontFamily: F.num, fontWeight: 700, fontSize: 13, color: c, letterSpacing: '-0.01em', ...TNUM }}>
        {pos ? '+' : '−'}{Math.abs(delta).toFixed(2)}%
      </span>
    </div>
  );
}

// Hero-Delta im Stil der Dashboard-Headcard: vertikaler Farbbalken + ▲/▼ große
// Prozentzahl + optionales Label. Deutlich größer & grafischer als DeltaChip.
function DeltaBlock({ delta, T, big = false, label }) {
  if (delta == null || !isFinite(delta)) return null;
  const pos = delta >= 0;
  const c = pos ? (T.gain || T.green) : (T.loss || T.red);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'stretch', gap: big ? 11 : 8 }}>
      <div style={{ width: 3, background: c, borderRadius: 2 }} />
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: big ? 6 : 3, lineHeight: 1 }}>
        <span style={{
          fontFamily: F.num, fontWeight: 700, fontSize: big ? 26 : 17,
          letterSpacing: '-0.03em', color: c,
          display: 'inline-flex', alignItems: 'center', gap: 6, ...TNUM,
        }}>
          <span style={{ fontSize: big ? 13 : 9 }}>{pos ? '▲' : '▼'}</span>
          {pos ? '+' : '−'}{Math.abs(delta).toFixed(2)}%
        </span>
        {label && (
          <span style={{
            fontFamily: F.mono, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.2em', color: T.mute,
          }}>{label}</span>
        )}
      </div>
    </div>
  );
}

function BtcDominanceTile({ T, t, withDivider, block, align }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const cached = cacheGet('mi_btcd');
    if (cached) setData(cached);
    let alive = true;
    // Server-Proxy (API-Key, kein Rate-Limit). Lokal (kein /api) → direkt.
    (async () => {
      let res = await fetch(`${_apiBase}/api/coin-markets?mode=global`);
      if (res.status === 404) res = await fetch('https://api.coingecko.com/api/v3/global');
      return res.json();
    })()
      .then(d => {
        if (!alive) return;
        const g = d?.data;
        if (!g) return;
        const mcp = g.market_cap_percentage || {};
        const btc = +mcp.btc || 0;
        const eth = +mcp.eth || 0;
        const stables = (+mcp.usdt || 0) + (+mcp.usdc || 0) + (+mcp.dai || 0) + (+mcp.usds || 0) + (+mcp.fdusd || 0);
        const rest = Math.max(0, 100 - btc - eth - stables);
        const totalMcap = g.total_market_cap?.usd || 0;
        const totalVol = g.total_volume?.usd || 0;
        const change24h = g.market_cap_change_percentage_24h_usd; // % change of total mcap (used as proxy)

        // Compare cached previous BTC% from yesterday to estimate dom24h
        const prev = cacheGet('mi_btcd_prev24h');
        let dom24h = null;
        if (prev && prev.btc != null && Date.now() - prev.t >= 20 * 60 * 60 * 1000) {
          dom24h = btc - prev.btc;
        }
        // Update rolling 24h snapshot opportunistically
        if (!prev || Date.now() - (prev.t || 0) > 22 * 60 * 60 * 1000) {
          try { localStorage.setItem('mi_btcd_prev24h', JSON.stringify({ t: Date.now(), btc })); } catch {}
        }

        const payload = {
          btc, eth, stables, rest,
          totalMcap, totalVol,
          change24h, dom24h,
        };
        setData(payload);
        cacheSet('mi_btcd', payload);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const target = data?.btc ?? null;
  const animVal = useAnimatedValue(target, 1100);
  const value = target == null ? 0 : animVal;
  const cls = target == null ? null : classify(BTCD_SCALE, target);
  const displayVal = target == null ? '··' : `${animVal.toFixed(1)}`;

  return (
    <>
      <IndexTile
        label="BTC·D"
        displayVal={displayVal}
        cls={cls}
        value={value}
        scale={BTCD_SCALE}
        T={T}
        t={t}
        onClick={() => data && setOpen(true)}
        disabled={!data}
        withDivider={withDivider}
        block={block}
        align={align}
      />
      {open && data && <BtcDomSheet data={data} onClose={() => setOpen(false)} T={T} t={t} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ALTCOIN SEASON
// ─────────────────────────────────────────────────────────────────────────────
const STABLE_RE = /^(USDT|USDC|DAI|TUSD|FDUSD|BUSD|USDS|PYUSD|FRAX|USDE|GUSD|LUSD)$/i;
const WRAPPED_RE = /^(WBTC|WETH|STETH|RETH|WSTETH|CBETH|WEETH|EZETH|RSETH|TBTC|BTCB|HBTC|MSETH|SFRXETH)$/i;

function AltseasonSheet({ data, onClose, T, t }) {
  const cls = classify(ALT_SCALE, data.value);
  const animVal = useAnimatedValue(data.value, 900);

  const topMax = Math.max(...data.topAlts.slice(0, 5).map(c => Math.abs(c.perf)), 1);

  return (
    <SheetFrame T={T} onClose={onClose} closeLabel={t('wid_close')} title={t('wid_alt_title_calm')} subtitle={t('wid_alt_sub')}>
      <SheetHero
        num={Math.round(animVal)} unit="/100"
        word={t(cls.labelKey)} wordColor={zoneColor(cls.color, T)}
        sub={t('wid_alt_beat_btc', { out: data.outperformers, total: data.sampleSize })}
        T={T} />

      <ReadingBar scale={ALT_SCALE} value={animVal} T={T}
                  loLabel={t('wid_scale_btc_season_lo')} hiLabel={t('wid_scale_altseason_hi')} />

      <div style={{ marginTop: 26 }}>
        <div style={{ fontFamily: F.display, fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em', color: T.text, marginBottom: 6 }}>
          {t('wid_top_outperformers')}
        </div>
        {data.topAlts.slice(0, 5).map((c, i) => (
          <PerfBar key={c.symbol} rank={i + 1} symbol={c.symbol} perf={c.perf} max={topMax} T={T} />
        ))}
      </div>

      <div style={{ marginTop: 20 }}>
        <RefRow T={T} t={t} label={t('wid_btc_30d')} val={`${data.btcPerf >= 0 ? '+' : '−'}${Math.abs(data.btcPerf).toFixed(1)} %`} />
      </div>

      <InfoCard T={T} title={t('wid_alt_info_title')}>
        {t('wid_alt_info_pre')}<strong style={{ color: T.text }}>{t('wid_30_days_inline')}</strong>{t('wid_alt_info_mid')}
        <strong style={{ color: T.text }}>≥ 75 %</strong>{t('wid_alt_info_mid2')}
        <strong style={{ color: T.text }}> ≤ 25 %</strong>{t('wid_alt_info_post')}
      </InfoCard>
      <div style={{
        marginTop: 16, fontFamily: F.family, fontSize: 11.5,
        color: T.faint || T.mute, textAlign: 'center', letterSpacing: '-0.005em',
      }}>{t('wid_alt_source')}</div>
    </SheetFrame>
  );
}

function AltseasonTile({ T, t, withDivider, block, align }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const cached = cacheGet('mi_alt');
    if (cached) setData(cached);
    let alive = true;
    // Server-Proxy (API-Key, CDN-Cache). Lokal (kein /api) → direkt CoinGecko.
    (async () => {
      let res = await fetch(`${_apiBase}/api/coin-markets?currency=usd&top=60&pct=30d`);
      if (res.status === 404) res = await fetch('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=60&page=1&price_change_percentage=30d');
      return res.json();
    })()
      .then(d => {
        if (!alive || !Array.isArray(d)) return;
        const btcEntry = d.find(c => (c.symbol || '').toUpperCase() === 'BTC');
        if (!btcEntry) return;
        const btcPerf = btcEntry.price_change_percentage_30d_in_currency;
        if (!isFinite(btcPerf)) return;

        const alts = d
          .filter(c => {
            const s = (c.symbol || '').toUpperCase();
            if (s === 'BTC') return false;
            if (STABLE_RE.test(s)) return false;
            if (WRAPPED_RE.test(s)) return false;
            return isFinite(c.price_change_percentage_30d_in_currency);
          })
          .map(c => ({
            symbol: (c.symbol || '').toUpperCase(),
            name: c.name,
            perf: c.price_change_percentage_30d_in_currency,
          }))
          .slice(0, 50);

        const outperformers = alts.filter(c => c.perf > btcPerf).length;
        const sampleSize = alts.length;
        const value = sampleSize > 0 ? Math.round((outperformers / sampleSize) * 100) : 50;
        const topAlts = [...alts].sort((a, b) => b.perf - a.perf);

        const payload = { value, outperformers, sampleSize, btcPerf, topAlts };
        setData(payload);
        cacheSet('mi_alt', payload);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const target = data?.value ?? null;
  const animVal = useAnimatedValue(target, 1100);
  const value = target == null ? 0 : animVal;
  const cls = target == null ? null : classify(ALT_SCALE, target);
  const displayVal = target == null ? '··' : Math.round(animVal);

  return (
    <>
      <IndexTile
        label="ALT"
        displayVal={displayVal}
        cls={cls}
        value={value}
        scale={ALT_SCALE}
        T={T}
        t={t}
        onClick={() => data && setOpen(true)}
        disabled={!data}
        withDivider={withDivider}
        block={block}
        align={align}
      />
      {open && data && <AltseasonSheet data={data} onClose={() => setOpen(false)} T={T} t={t} />}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GESAMTMARKT (Total market cap) — Eyecatcher + Treemap-Sheet
// ─────────────────────────────────────────────────────────────────────────────
function TotalMcapSheet({ data, onClose, T, t }) {
  const { convert, symbol, locale, fxRate } = useCurrency();
  const animVal = useAnimatedValue(data.totalMcap, 900); // Global-Endpoint = USD
  const toEur = (usd) => (Number.isFinite(usd) && fxRate ? usd / fxRate : usd);
  const hero = bigParts(toEur(animVal), convert, symbol, locale, t);

  // Komposition in fester Reihenfolge (= MassBar im Tile), Calm-Farben.
  const comp = [
    { key: 'btc',     label: 'Bitcoin',            pct: data.pct.btc,     color: T.gold },
    { key: 'eth',     label: 'Ethereum',           pct: data.pct.eth,     color: T.silver },
    { key: 'stables', label: t('wid_stablecoins'), pct: data.pct.stables, color: T.gain || T.green },
    { key: 'rest',    label: t('wid_rest_market'), pct: data.pct.rest,    color: T.mute },
  ];

  const exclBtc = data.totalMcap * (1 - Math.max(0, data.pct.btc) / 100);
  const volStr = fmtBig(toEur(data.totalVol), convert, symbol, locale, t);
  const exclBtcStr = fmtBig(toEur(exclBtc), convert, symbol, locale, t);

  // ── Interaktiver Gesamtmarkt-Verlauf ──────────────────────────────────────
  const RANGES = [
    { v: 7,   label: t('wid_r_7d') },
    { v: 30,  label: t('wid_r_30d') },
    { v: 90,  label: t('wid_r_90d') },
    { v: 365, label: t('wid_r_1y') },
  ];
  const [range, setRange] = useState(30);
  const [hist, setHist] = useState(null);     // rohe Summen-Reihe (USD): [[ts, v]]
  const [histLoading, setHistLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setHistLoading(true);
    (async () => {
      let res = await fetch(`${_apiBase}/api/coin-markets?mode=mcapchart&days=${range}`);
      if (res.status === 404) {
        // Lokal (kein /api): BTC-Reihe direkt als grobe Form-Proxy.
        res = await fetch(`https://api.coingecko.com/api/v3/coins/bitcoin/market_chart?vs_currency=usd&days=${range}`);
        if (!res.ok) throw new Error('no');
        const j = await res.json();
        return { series: Array.isArray(j?.market_caps) ? j.market_caps : null };
      }
      if (!res.ok) throw new Error('no');
      return res.json();
    })()
      .then(d => { if (!alive) return; setHist(Array.isArray(d?.series) ? d.series : null); setHistLoading(false); })
      .catch(() => { if (!alive) return; setHist(null); setHistLoading(false); });
    return () => { alive = false; };
  }, [range]);

  // Letzten Punkt exakt auf die angezeigte Gesamt-Marktkap. skalieren → Verlaufs-
  // FORM echt, Niveau am rechten Rand deckt sich mit der Hero-Zahl oben.
  const scaled = useMemo(() => {
    if (!hist || hist.length < 2) return null;
    const last = hist[hist.length - 1][1];
    const factor = (last > 0 && Number.isFinite(data.totalMcap)) ? data.totalMcap / last : 1;
    return hist.map(([ts, v]) => [ts, v * factor]);
  }, [hist, data.totalMcap]);
  const rangeChange = scaled && scaled.length >= 2
    ? ((scaled[scaled.length - 1][1] - scaled[0][1]) / scaled[0][1]) * 100
    : null;
  const fmtChartVal = (usd) => { const p = bigParts(toEur(usd), convert, symbol, locale, t); return `${p.num} ${p.unit}`; };
  const fmtChartTime = (ts) => new Date(ts).toLocaleDateString(locale, { day: '2-digit', month: 'short', ...(range >= 365 ? { year: '2-digit' } : {}) });
  const rangeLabel = (RANGES.find(r => r.v === range) || {}).label;

  const ch = data.change24h;
  const chValid = ch != null && isFinite(ch);
  const chWord = chValid ? `${ch >= 0 ? '+' : '−'}${Math.abs(ch).toFixed(2)} %` : null;
  const chColor = chValid ? (ch >= 0 ? (T.gain || T.green) : (T.loss || T.red)) : T.text;

  return (
    <SheetFrame T={T} onClose={onClose} closeLabel={t('wid_close')} title={t('wid_total_tile')} subtitle={t('wid_total_sub')}>
      <SheetHero
        num={hero.num} unit={hero.unit}
        word={chWord} wordColor={chColor}
        sub={chValid ? t('wid_last_24h') : null}
        T={T} />

      {/* Lesebalken = Komposition (statt Marker): Markt-Masse als Anteilsband. */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', gap: 3, height: 14, borderRadius: 6, overflow: 'hidden', background: T.line }}>
          {comp.map((s) => (
            <div key={s.key} style={{ width: `${Math.max(0, s.pct)}%`, height: '100%', background: s.color }} />
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginTop: 13 }}>
          {comp.map((s) => (
            <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: F.family, fontSize: 12.5, fontWeight: 500, color: T.sub }}>
              <i style={{ width: 9, height: 9, borderRadius: 2, background: s.color }} />
              {s.label} <b style={{ color: T.text, fontWeight: 700, ...TNUM }}>{s.pct.toFixed(1)} %</b>
            </span>
          ))}
        </div>
      </div>

      {/* Interaktiver Verlauf — der Kunde scrubbt selbst durch die Historie
          (Crosshair + Datum/Wert-Tooltip) und wechselt den Zeitraum. */}
      <div style={{ marginTop: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{
            fontFamily: F.display, fontSize: 14.5, fontWeight: 700,
            letterSpacing: '-0.01em', color: T.text,
          }}>{t('wid_total_history')}</span>
          <Segmented bare value={range} onChange={setRange}
                     options={RANGES.map(r => ({ value: r.v, label: r.label }))} />
        </div>
        {scaled ? (
          <>
            {rangeChange != null && (
              <div style={{ marginBottom: 12 }}>
                <DeltaBlock delta={rangeChange} T={T} label={rangeLabel} />
              </div>
            )}
            <BigAreaChart
              values={scaled}
              color={rangeChange >= 0 ? (T.gain || T.green) : (T.loss || T.red)}
              formatPrice={fmtChartVal}
              formatTime={fmtChartTime}
              h={176}
            />
          </>
        ) : (
          <div style={{
            height: 176, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.mute, fontFamily: F.mono, fontSize: 11, letterSpacing: '0.12em',
          }}>{histLoading ? '· · ·' : t('wid_no_history')}</div>
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        <RefRow T={T} t={t} label={t('wid_total_alt_mcap')} val={exclBtcStr} />
        <RefRow T={T} t={t} label={t('wid_vol_24h')} val={volStr} />
        {data.activeCryptos != null && (
          <RefRow T={T} t={t} label={t('wid_total_assets')} val={fmt(data.activeCryptos, locale)} />
        )}
      </div>

      <InfoCard T={T} title={t('wid_total_info_title')}>
        {t('wid_total_info_body')}
      </InfoCard>
      <div style={{
        marginTop: 16, fontFamily: F.family, fontSize: 11.5,
        color: T.faint || T.mute, textAlign: 'center', letterSpacing: '-0.005em',
      }}>{t('wid_source', { src: 'CoinGecko' })}</div>
    </SheetFrame>
  );
}

function TotalMcapTile({ T, t, variant = 'inline' }) {
  const { convert, symbol, locale, fxRate } = useCurrency();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    const cached = cacheGet('mi_total');
    if (cached) setData(cached);
    let alive = true;
    (async () => {
      let res = await fetch(`${_apiBase}/api/coin-markets?mode=global`);
      if (res.status === 404) res = await fetch('https://api.coingecko.com/api/v3/global');
      return res.json();
    })()
      .then(d => {
        if (!alive) return;
        const g = d?.data;
        if (!g) return;
        const mcp = g.market_cap_percentage || {};
        const btc = +mcp.btc || 0;
        const eth = +mcp.eth || 0;
        const stables = (+mcp.usdt || 0) + (+mcp.usdc || 0) + (+mcp.dai || 0) + (+mcp.usds || 0) + (+mcp.fdusd || 0);
        const rest = Math.max(0, 100 - btc - eth - stables);
        const payload = {
          totalMcap: g.total_market_cap?.usd || 0,
          totalVol: g.total_volume?.usd || 0,
          change24h: g.market_cap_change_percentage_24h_usd ?? null,
          activeCryptos: g.active_cryptocurrencies ?? null,
          pct: { btc, eth, stables, rest },
        };
        setData(payload);
        cacheSet('mi_total', payload);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const animVal = useAnimatedValue(data ? data.totalMcap : null, 1100);
  const toEur = (usd) => (Number.isFinite(usd) && fxRate ? usd / fxRate : usd);
  const parts = data ? bigParts(toEur(animVal), convert, symbol, locale, t) : { num: '··', unit: '' };
  const banner = variant === 'banner';

  const label = (
    <div style={{
      fontFamily: F.family, fontSize: 11, fontWeight: 500,
      letterSpacing: '-0.005em', lineHeight: 1, color: T.sub,
    }}>{t('wid_total_tile')}</div>
  );
  const number = (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
      <span style={{
        fontFamily: F.display, fontSize: banner ? 34 : 27, fontWeight: 800,
        letterSpacing: '-0.05em', color: T.text, lineHeight: 1, ...TNUM,
      }}>{parts.num}</span>
      {parts.unit && (
        <span style={{
          fontFamily: F.display, fontSize: banner ? 15 : 13, fontWeight: 700,
          letterSpacing: '-0.02em', color: T.sub,
        }}>{parts.unit}</span>
      )}
    </span>
  );

  const press = {
    onMouseDown: () => setPressed(true),
    onMouseUp: () => setPressed(false),
    onMouseLeave: () => { setPressed(false); setHover(false); },
    onMouseEnter: () => setHover(true),
    onTouchStart: () => setPressed(true),
    onTouchEnd: () => setPressed(false),
  };
  const baseBtn = {
    appearance: 'none', background: 'transparent', border: 'none',
    cursor: data ? 'pointer' : 'default', textAlign: 'left',
    transition: 'transform 130ms ease-out, opacity 200ms ease',
    transform: pressed ? 'scale(0.98)' : 'scale(1)',
    opacity: data ? (hover ? 0.92 : 1) : 0.5,
    WebkitTapHighlightColor: 'transparent',
  };

  return (
    <>
      {banner ? (
        <button onClick={() => data && setOpen(true)} disabled={!data} {...press}
          className="mo-row"
          aria-label={`${t('wid_total_tile')}: ${parts.num} ${parts.unit}`}
          style={{ ...baseBtn, width: '100%', display: 'flex', flexDirection: 'column', gap: 10, padding: 0 }}>
          {label}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 12 }}>
            {number}
            <TrendArrow delta={data?.change24h} T={T} />
          </div>
          <MassBar pct={data ? data.pct : { btc: 0, eth: 0, stables: 0, rest: 0 }} T={T} width="100%" height={8} />
        </button>
      ) : (
        <button onClick={() => data && setOpen(true)} disabled={!data} {...press}
          className="mo-row"
          aria-label={`${t('wid_total_tile')}: ${parts.num} ${parts.unit}`}
          style={{ ...baseBtn, width: '100%', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 7, padding: '2px 18px' }}>
          {label}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            {number}
            <TrendArrow delta={data?.change24h} T={T} />
          </div>
          <MassBar pct={data ? data.pct : { btc: 0, eth: 0, stables: 0, rest: 0 }} T={T} width={172} height={7} />
        </button>
      )}
      {open && data && <TotalMcapSheet data={data} onClose={() => setOpen(false)} T={T} t={t} />}
    </>
  );
}

// ── Strip wrapper ───────────────────────────────────────────────────────────
// variant="inline" → Desktop: volle Breite als EIN Band — Gesamtmarkt (Headline-
//   Zahl) breiter links, die drei Gauges gleichmäßig nach rechts verteilt, durch
//   senkrechte Haarlinien zu einem zusammenhängenden Instrumenten-Band gebunden.
// variant="strip"  → Mobile: Gesamtmarkt als volle Breite oben, darunter die
//   drei gleichbreiten Index-Spalten in eigener Reihe.
export default function MarketIndices({ variant = 'inline' }) {
  const { T } = useTheme();
  const { t } = useT();
  const block = variant === 'strip';

  if (block) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 14 }}>
        <TotalMcapTile T={T} t={t} variant="banner" />
        <div style={{ display: 'flex', width: '100%', alignItems: 'stretch', gap: 0,
                      borderTop: `1px solid ${T.line}`, paddingTop: 12 }}>
          <BtcDominanceTile T={T} t={t} withDivider={false} block />
          <AltseasonTile T={T} t={t} withDivider block />
          <FearGreedTile T={T} t={t} withDivider block />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid', width: '100%',
      gridTemplateColumns: '1.5fr 1fr 1fr 1fr', alignItems: 'center',
    }}>
      <TotalMcapTile T={T} t={t} variant="inline" />
      <BtcDominanceTile T={T} t={t} withDivider align="center" />
      <AltseasonTile T={T} t={t} withDivider align="center" />
      <FearGreedTile T={T} t={t} withDivider align="center" />
    </div>
  );
}
